import { useState, useEffect, useCallback, useRef } from "react";
import { UserData } from "../../../../types/org.type";
import { checkWallexStatus } from "../Service/ApiServices";
import { ScreeningStatus } from "../Components/Enum";

interface UseWallexStatusInput {
  userId: number | undefined;
  user: UserData[] | undefined;
  token: string;
}

export const useWallexStatus = ({
  userId,
  user,
  token,
}: UseWallexStatusInput) => {
  const [screeningStatus, setScreeningStatus] = useState<string | null>(null);
  const [screeningRemarks, setScreeningRemarks] = useState<string | null>(null);
  const [hasBeenSentToWallex, setHasBeenSentToWallex] =
    useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [uploadPartialSuccess, setUploadPartialSuccess] = useState(false);
  const [failedDocuments, setFailedDocuments] = useState<{ fileName?: string; errorMessage?: string; error?: string; supportMessage?: string }[]>([]);
  const [wallexPassportPhotos, setWallexPassportPhotos] = useState<
    Record<string, { ownerId: string; data: string; passportUserId?: string }>
  >({});
  const [excludedFiles, setExcludedFiles] = useState<string[]>([]);
  const [lastUploadTimestamp, setLastUploadTimestamp] = useState<string | null>(
    null
  );
  const lastFetchRef = useRef<number>(0);

  // Process partial success results
  const processSuccessfulUploads = useCallback((results: { status: string; fileName: string }[] | null | undefined) => {
    if (!results) return;

    const successfulFiles = results
      .filter(
        (r: { status: string; fileName: string }) => r.status === "success"
      )
      .map((r: { status: string; fileName: string }) => r.fileName);

    if (successfulFiles.length > 0) {
      setUploadedFiles((prevFiles) => {
        const uniqueFiles = new Set([...prevFiles, ...successfulFiles]);
        return Array.from(uniqueFiles);
      });
    }
  }, []);

  // Refresh passport photos after partial failure
  const refreshPassportPhotos = useCallback(async () => {
    if (!userId) return;

    try {
      const response = await checkWallexStatus(userId, token);
      if (response && response.passportPhotos) {
        setWallexPassportPhotos(response.passportPhotos);
      }
    } catch (error) {
      console.error("Error refreshing national identity photos:", error);
    }
  }, [userId, token]);

  // Update timestamp from response
  const updateLastTimestamp = useCallback(
    (timestamps: Record<string, string>) => {
      const timestampValues = Object.values(timestamps) as string[];
      if (timestampValues.length > 0) {
        const mostRecentTimestamp = new Date(
          Math.max(...timestampValues.map((ts) => new Date(ts).getTime()))
        );
        setLastUploadTimestamp(mostRecentTimestamp.toISOString());
      }
    },
    []
  );

  // Refresh data from server after upload
  const refreshDataFromServer = useCallback(async () => {
    if (!userId) return;

    try {
      const response = await checkWallexStatus(userId, token);

      if (Array.isArray(response.uploadedDocuments)) {
        setUploadedFiles(response.uploadedDocuments);
      }

      if (response.passportPhotos) {
        setWallexPassportPhotos(response.passportPhotos);
      }

      if (response.documentTimestamps) {
        updateLastTimestamp(response.documentTimestamps);
      }

      if (response.screeningStatus) {
        setScreeningStatus(response.screeningStatus);
      }
    } catch (error) {
      console.error("Error refreshing data after upload:", error);
    }
  }, [userId, token, updateLastTimestamp]);

  // Check document and Wallex status — extracted to useCallback to avoid stale closure in focus handler
  const checkDocAndWallexStatus = useCallback(async () => {
    if (!userId) return;

    // Debounce: skip if called within the last 5 seconds (prevents rapid focus refetches)
    const now = Date.now();
    if (now - lastFetchRef.current < 5000) return;
    lastFetchRef.current = now;

    try {
      const response = await checkWallexStatus(userId, token);

      // Set excluded files if there are any
      if (response.excludedFiles && response.excludedFiles.length > 0) {
        setExcludedFiles(response.excludedFiles);
      }
      // First check the screening status
      if (response.screeningStatus) {
        setScreeningStatus(response.screeningStatus);

        if (
          response.screeningStatus === ScreeningStatus.DOCNEEDRESUBMISSION
        ) {
          // Documents need resubmission — allow re-upload
          setHasBeenSentToWallex(false);
          setUploadPartialSuccess(true);
        } else if (
          Object.values(ScreeningStatus).includes(
            response.screeningStatus as ScreeningStatus
          )
        ) {
          setHasBeenSentToWallex(true);
          setUploadPartialSuccess(false);
        }
      }

      // Then check for current failed documents
      else if (
        (response.failedUploads && response.failedUploads > 0) ||
        (response.failedDocuments && response.failedDocuments.length > 0)
      ) {
        setHasBeenSentToWallex(true);
        setUploadPartialSuccess(true);

        if (response.failedDocuments) {
          setFailedDocuments(response.failedDocuments);
        } else if (response.results) {
          const failed = response.results
            .filter((item) => item.status === "error")
            .map((item) => ({ fileName: item.fileName }));

          setFailedDocuments(failed);
        }
      } else {
        setHasBeenSentToWallex(true);
        setUploadPartialSuccess(false);
      }

      // Update uploaded files
      if (Array.isArray(response.uploadedDocuments)) {
        setUploadedFiles(response.uploadedDocuments);
      }

      // Update passport photos
      if (response.passportPhotos) {
        setWallexPassportPhotos(response.passportPhotos);

        // Auto-exclude already uploaded passport photos
        // Use functional update to avoid stale closure on excludedFiles
        const passportPhotos = response.passportPhotos;
        setExcludedFiles((currentExcludedFiles) => {
          const newExcludedFiles = [...currentExcludedFiles];

          Object.entries(passportPhotos).forEach(([, photo]) => {
            const typedPhoto = photo as { ownerId: string; data: string };
            const userIdFromPhoto = typedPhoto.ownerId;

            const matchingUser = user?.find(
              (u) => u.id.toString() === userIdFromPhoto
            );

            if (matchingUser?.didPassportPhoto) {
              const uniqueIdentifier = `user_${userIdFromPhoto}_photo_${matchingUser.didPassportPhoto}`;

              if (!newExcludedFiles.includes(uniqueIdentifier)) {
                newExcludedFiles.push(uniqueIdentifier);
              }
            }
          });

          return newExcludedFiles.length > currentExcludedFiles.length
            ? newExcludedFiles
            : currentExcludedFiles;
        });
      }

      // Update screening remarks
      if (response.screeningRemarks) {
        setScreeningRemarks(response.screeningRemarks);
      }

      // Update last upload timestamp
      if (response.registrationDate) {
        setLastUploadTimestamp(response.registrationDate);
      }

      if (response.uploadStatus === "Success") {
        setHasBeenSentToWallex(true);
      }
    } catch (error) {
      console.error("Error checking Wallex status:", error);
    }
  }, [userId, token, user]);

  // Check Wallex status on mount and window focus
  useEffect(() => {
    checkDocAndWallexStatus();

    window.addEventListener("focus", checkDocAndWallexStatus);

    return () => {
      window.removeEventListener("focus", checkDocAndWallexStatus);
    };
  }, [checkDocAndWallexStatus]);

  return {
    // State
    screeningStatus,
    screeningRemarks,
    hasBeenSentToWallex,
    uploadedFiles,
    uploadPartialSuccess,
    failedDocuments,
    wallexPassportPhotos,
    excludedFiles,
    lastUploadTimestamp,

    // Setters
    setScreeningStatus,
    setScreeningRemarks,
    setHasBeenSentToWallex,
    setUploadedFiles,
    setUploadPartialSuccess,
    setFailedDocuments,
    setWallexPassportPhotos,
    setExcludedFiles,
    setLastUploadTimestamp,

    // Functions
    processSuccessfulUploads,
    refreshPassportPhotos,
    refreshDataFromServer,
    updateLastTimestamp,
  };
};
